// Zero-knowledge config crypto. Key derived in-browser from a passphrase; never sent to Mezzo.
// Blob layout (base64): salt(16) || iv(12) || ciphertext+tag.

const SALT_BYTES = 16;
const IV_BYTES = 12;
/** AES-GCM authentication tag length (bytes). */
const TAG_BYTES = 16;
const MIN_BLOB_BYTES = SALT_BYTES + IV_BYTES + TAG_BYTES;
const PBKDF2_ITERATIONS = 210_000;
const KEY_BITS = 256;

/** Thrown when the stored blob is not structurally valid (not when the passphrase is wrong). */
export class MalformedBlobError extends Error {
  constructor(message = 'Configuration data is malformed.') {
    super(message);
    this.name = 'MalformedBlobError';
  }
}

const toBase64 = (bytes: Uint8Array): string =>
  btoa(String.fromCharCode(...Array.from(bytes)));

const fromBase64 = (base64: string): Uint8Array => {
  try {
    return Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));
  } catch {
    throw new MalformedBlobError('Configuration data is not valid base64.');
  }
};

const deriveKey = async ({ passphrase, salt }: { passphrase: string; salt: Uint8Array }): Promise<CryptoKey> => {
  const baseKey = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(passphrase),
    'PBKDF2',
    false,
    ['deriveKey'],
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations: PBKDF2_ITERATIONS, hash: 'SHA-256' },
    baseKey,
    { name: 'AES-GCM', length: KEY_BITS },
    false,
    ['encrypt', 'decrypt'],
  );
};

export const encryptConfigBlob = async ({ plaintext, passphrase }: { plaintext: string; passphrase: string }): Promise<string> => {
  const salt = crypto.getRandomValues(new Uint8Array(SALT_BYTES));
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const key = await deriveKey({ passphrase, salt });
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, new TextEncoder().encode(plaintext)),
  );

  const combined = new Uint8Array(salt.length + iv.length + ciphertext.length);
  combined.set(salt, 0);
  combined.set(iv, salt.length);
  combined.set(ciphertext, salt.length + iv.length);
  return toBase64(combined);
};

export const decryptConfigBlob = async ({ blob, passphrase }: { blob: string; passphrase: string }): Promise<string> => {
  if (typeof blob !== 'string' || blob.trim().length === 0) {
    throw new MalformedBlobError('Configuration data is empty.');
  }

  const bytes = fromBase64(blob);
  if (bytes.length < MIN_BLOB_BYTES) {
    throw new MalformedBlobError('Configuration data is too short to be a valid encrypted blob.');
  }

  const salt = bytes.slice(0, SALT_BYTES);
  const iv = bytes.slice(SALT_BYTES, SALT_BYTES + IV_BYTES);
  const ciphertext = bytes.slice(SALT_BYTES + IV_BYTES);
  const key = await deriveKey({ passphrase, salt });
  const plaintext = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ciphertext);
  return new TextDecoder().decode(plaintext);
};
